/**
 * ScoringDashboard (Values page)
 *
 * Full-width table showing active items and their attribute coverage.
 * Clicking a row opens a right-side slide-out panel with the ValuesTab
 * for inline value editing — same interaction model as the item list.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Search,
  ExternalLink,
  Info,
  X,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { InfoModal, type InfoSection } from '../../../components/prioritization/InfoModal';
import { ValuesTab } from '../../../components/prioritization/item-view/activity/ValuesTab';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../../lib/itemCategoryColors';

// ── Coverage dot ──────────────────────────────────────────────────────────────

function CoverageDot({ hasValue, label }: { hasValue: boolean; label: string }) {
  return (
    <span
      title={label}
      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
        hasValue ? 'bg-[var(--green)]' : 'bg-[var(--mustard)]'
      }`}
    />
  );
}

// ── Values panel (right slide-out) ────────────────────────────────────────────

function ValuesPanel({
  itemId,
  itemTitle,
  userId,
  onClose,
}: {
  itemId: string;
  itemTitle: string;
  userId: string | null;
  onClose: () => void;
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--rule)] px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-[var(--text-ter)] mb-0.5">Attribute values</p>
            <h2 className="text-sm font-semibold text-[var(--text-pri)] line-clamp-2">{itemTitle}</h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to={`/prioritization/items/${itemId}`}
              className="flex items-center gap-1 border border-[var(--rule)] px-2.5 py-1.5 text-xs text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
              title="Open full item"
            >
              Open <ExternalLink className="h-3 w-3" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Values editor */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <ValuesTab itemId={itemId} canAccept={!!userId} readOnly={!userId} />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ScoringDashboard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const acceptedValues = useValueProposalStore((s) => s.acceptedValues);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId);

  useEffect(() => {
    fetchItems();
    fetchAttributes();
    fetchProposals();
  }, [fetchItems, fetchAttributes, fetchProposals]);

  const eligibleItems = useMemo(
    () => items.filter((c) => (c as any).exit_status === 'active'),
    [items]
  );

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return eligibleItems;
    return eligibleItems.filter(
      (c) => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)
    );
  }, [eligibleItems, searchQuery]);

  // Map itemId → Set<attributeKey> of covered attributes
  const coveredMap = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const av of acceptedValues) {
      if (!m.has(av.itemId)) m.set(av.itemId, new Set());
      m.get(av.itemId)!.add(av.attributeKey);
    }
    return m;
  }, [acceptedValues]);

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedItemId),
    [items, selectedItemId]
  );

  const hasAttributes = attributes.length > 0;
  const hasItems = filteredItems.length > 0;

  const SCORING_GUIDE_SECTIONS: InfoSection[] = [
    {
      id: 'what',
      label: 'What is this page?',
      tagline: 'Directional Planning signals for prioritization.',
      description:
        'This page captures the rough constraints, risks, and confidence signals needed to compare options responsibly. These values inform priority — they do not plan execution. Directional Planning happens before priorities are accepted. Detailed Planning happens after.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    },
    {
      id: 'dots',
      label: 'Coverage dots',
      tagline: 'Quick visual on which attributes have values.',
      description:
        'Each dot represents one configured attribute. Green = value set. Amber = no value yet. Hover to see the attribute name.',
      color: 'bg-[var(--surface)] border-[var(--rule)]',
    },
    {
      id: 'score-vs-estimate',
      label: 'Score vs Estimate',
      tagline: 'Two modes for entering values.',
      description:
        '"Score" sets the authoritative accepted value immediately. "Estimate" adds your opinion to a pool — useful when multiple people should weigh in before committing.',
      color: 'bg-[var(--surface)] border-[var(--rule)]',
    },
  ];

  // ── Shared header ─────────────────────────────────────────────────────────

  const header = (
    <div className="flex-shrink-0 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-4">
      <PageHeader
        icon={<BarChart3 className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Item Values"
        subtitle="Directional Planning signals. Click an item to set or update values that inform prioritization."
        actions={
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] bg-[var(--bg-raised)] hover:bg-[var(--slate-dim)] border border-[var(--rule)] transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
            How values work
          </button>
        }
      />
    </div>
  );

  // ── Zero: no attributes ───────────────────────────────────────────────────

  if (!hasAttributes) {
    return (
      <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden">
        {header}
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-8 py-10 max-w-md text-center">
            <BarChart3 className="h-8 w-8 text-[var(--mustard)] mx-auto mb-3" />
            <p className="font-medium text-[var(--text-pri)] mb-1">No scoring attributes configured</p>
            <p className="text-sm text-[var(--text-ter)] mb-4">
              Add at least one attribute before scoring items.
            </p>
            <Link
              to="/prioritization/settings/attributes"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--mustard)] text-white text-sm font-medium hover:bg-[var(--mustard)] transition-colors"
            >
              Set up attributes <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        {showGuide && <InfoModal sections={SCORING_GUIDE_SECTIONS} onClose={() => setShowGuide(false)} />}
      </div>
    );
  }

  // ── Zero: attributes exist but no items ────────────────────────────────────

  if (!hasItems && !searchQuery) {
    return (
      <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden">
        {header}
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-8 py-10 max-w-md text-center">
            <BarChart3 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
            <p className="font-medium text-[var(--text-pri)] mb-1">No active items yet</p>
            <p className="text-sm text-[var(--text-ter)] mb-4">
              Create items from the Items page, then return here to score them.
            </p>
            <Link
              to="/prioritization/items"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] transition-colors"
            >
              Go to Items <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        {showGuide && <InfoModal sections={SCORING_GUIDE_SECTIONS} onClose={() => setShowGuide(false)} />}
      </div>
    );
  }

  // ── Table + panel ──────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden">
      {header}

      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-3 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-2.5">
        <div className="flex items-center gap-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-1.5 w-64">
          <Search className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items…"
            className="flex-1 bg-transparent text-sm focus:outline-none"
          />
        </div>
        <span className="text-xs text-[var(--text-ter)]">{filteredItems.length} items</span>
        <Link
          to="/prioritization/settings/attributes"
          className="ml-auto text-xs text-[var(--text-ter)] hover:underline flex items-center gap-0.5"
        >
          Manage attributes <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* Body: table + slide-out panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Table */}
        <div className="flex-1 min-w-0 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10 bg-[var(--surface)] border-b border-[var(--rule)]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide w-full">Title</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide whitespace-nowrap">Category</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide whitespace-nowrap">
                  Coverage
                  <span className="ml-1.5 text-[10px] font-normal text-[var(--text-ter)] normal-case tracking-normal">
                    ({attributes.length} attr{attributes.length !== 1 ? 's' : ''})
                  </span>
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-[var(--text-ter)]">
                    No items match "{searchQuery}".{' '}
                    <button
                      type="button"
                      className="text-[var(--text-ter)] hover:underline"
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </button>
                  </td>
                </tr>
              )}
              {filteredItems.map((item) => {
                const covered = coveredMap.get(item.id) ?? new Set<string>();
                const isOpen = item.id === selectedItemId;
                const category = (item as any).itemCategory as string | undefined;
                const badgeCls = category
                  ? (CATEGORY_BADGE[category] ?? CATEGORY_BADGE_FALLBACK)
                  : CATEGORY_BADGE_FALLBACK;
                const coveredCount = covered.size;
                const totalCount = attributes.length;

                return (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedItemId(isOpen ? null : item.id)}
                    className={[
                      'cursor-pointer border-b border-[var(--rule)]/60 transition-colors group',
                      isOpen
                        ? 'bg-[var(--slate-dim)] border-l-2 border-l-indigo-500'
                        : 'hover:bg-[var(--surface)]/50 dark:hover:bg-[var(--hover-tint)] border-l-2 border-l-transparent',
                    ].join(' ')}
                  >
                    {/* Title */}
                    <td className="px-4 py-3">
                      <p className={`font-medium leading-snug ${isOpen ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                        {item.title || 'Untitled'}
                      </p>
                      {item.description && (
                        <p className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-1">{item.description}</p>
                      )}
                    </td>

                    {/* Category badge */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {category ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeCls}`}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--text-ter)]">—</span>
                      )}
                    </td>

                    {/* Coverage dots + ratio */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-0.5">
                          {attributes.map((attr) => (
                            <CoverageDot
                              key={attr.key}
                              hasValue={covered.has(attr.key)}
                              label={`${attr.label}${attr.required ? ' (required)' : ''}`}
                            />
                          ))}
                        </div>
                        <span className={`text-xs font-medium ml-1 ${
                          coveredCount === totalCount ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
                          coveredCount > 0 ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)]'
                        }`}>
                          {coveredCount}/{totalCount}
                        </span>
                      </div>
                    </td>

                    {/* Open link */}
                    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={`/prioritization/items/${item.id}`}
                        className="flex items-center justify-center rounded p-1 text-[var(--text-ter)] opacity-0 group-hover:opacity-100 hover:bg-[var(--hover-tint)] hover:text-[var(--text-ter)] transition-all"
                        title="Open item"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Right slide-out panel */}
        <div
          className={`flex-shrink-0 border-l border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden transition-all duration-200 ${
            selectedItem ? 'w-[420px]' : 'w-0'
          }`}
        >
          {selectedItem && (
            <ValuesPanel
              itemId={selectedItem.id}
              itemTitle={selectedItem.title ?? 'Untitled'}
              userId={userId}
              onClose={() => setSelectedItemId(null)}
            />
          )}
        </div>
      </div>

      {showGuide && <InfoModal sections={SCORING_GUIDE_SECTIONS} onClose={() => setShowGuide(false)} />}
    </div>
  );
}
